<template>
  <aside
    class="natcon-sidebar-wrapper"
    :class="{ toggled: toggled, 'test-mode-active': TestMode }"
  >
    <!-- Animated Particle Flow Background Layer (Concept 4) -->
    <div class="sidebar-particle-flow-layer" aria-hidden="true">
      <!-- Layer 1: Flowing Light Streams (SVG Organic Energy Curves) -->
      <svg
        class="energy-streams-svg"
        viewBox="0 0 270 900"
        preserveAspectRatio="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <linearGradient id="streamGrad1" x1="0%" y1="100%" x2="0%" y2="0%">
            <stop offset="0%" stop-color="#FFFFFF" stop-opacity="0" />
            <stop offset="25%" stop-color="#FFFFFF" stop-opacity="0.25" />
            <stop offset="50%" stop-color="#FFFFFF" stop-opacity="0.95" />
            <stop offset="75%" stop-color="#FFFFFF" stop-opacity="0.25" />
            <stop offset="100%" stop-color="#FFFFFF" stop-opacity="0" />
          </linearGradient>
          <linearGradient id="streamGrad2" x1="0%" y1="100%" x2="0%" y2="0%">
            <stop offset="0%" stop-color="#FFFFFF" stop-opacity="0" />
            <stop offset="30%" stop-color="#FFFFFF" stop-opacity="0.2" />
            <stop offset="60%" stop-color="#FFFFFF" stop-opacity="0.98" />
            <stop offset="85%" stop-color="#FFFFFF" stop-opacity="0.2" />
            <stop offset="100%" stop-color="#FFFFFF" stop-opacity="0" />
          </linearGradient>
          <filter id="glowFilter" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="2" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        <path
          class="stream-line stream-1"
          d="M 30 900 C 70 700, 10 500, 60 300 C 110 100, 40 0, 40 0"
          stroke="url(#streamGrad1)"
          filter="url(#glowFilter)"
        />
        <path
          class="stream-line stream-2"
          d="M 120 900 C 80 650, 180 450, 130 250 C 90 80, 160 0, 160 0"
          stroke="url(#streamGrad2)"
          filter="url(#glowFilter)"
        />
        <path
          class="stream-line stream-3"
          d="M 210 900 C 170 720, 230 480, 180 280 C 140 120, 220 0, 220 0"
          stroke="url(#streamGrad1)"
          filter="url(#glowFilter)"
        />
        <path
          class="stream-line stream-4"
          d="M 70 900 C 130 680, 50 420, 110 200 C 150 50, 90 0, 90 0"
          stroke="url(#streamGrad2)"
          filter="url(#glowFilter)"
        />
        <path
          class="stream-line stream-5"
          d="M 170 900 C 220 640, 140 400, 200 180 C 240 60, 180 0, 180 0"
          stroke="url(#streamGrad1)"
          filter="url(#glowFilter)"
        />
      </svg>

      <!-- Layer 2: Network Connection Lines -->
      <svg
        class="network-lines-svg"
        viewBox="0 0 270 900"
        preserveAspectRatio="none"
      >
        <line
          class="net-line net-1"
          x1="45"
          y1="220"
          x2="135"
          y2="310"
          stroke="#0097D7"
          opacity="0.25"
        />
        <line
          class="net-line net-2"
          x1="135"
          y1="310"
          x2="190"
          y2="440"
          stroke="#56BDA3"
          opacity="0.3"
        />
        <line
          class="net-line net-3"
          x1="70"
          y1="520"
          x2="170"
          y2="610"
          stroke="#0097D7"
          opacity="0.25"
        />
        <line
          class="net-line net-4"
          x1="170"
          y1="610"
          x2="110"
          y2="760"
          stroke="#3A67B1"
          opacity="0.3"
        />
        <line
          class="net-line net-5"
          x1="110"
          y1="760"
          x2="210"
          y2="840"
          stroke="#EDBE38"
          opacity="0.2"
        />
      </svg>

      <!-- Layer 3 & 4: Glowing Particles & Bright Traveling Nodes -->
      <div class="particles-container">
        <div class="particle p-1 node-aqua"></div>
        <div class="particle p-2 node-seafoam"></div>
        <div class="particle p-3 node-aqua"></div>
        <div class="particle p-4 node-gold"></div>
        <div class="particle p-5 node-navy"></div>
        <div class="particle p-6 node-aqua"></div>
        <div class="particle p-7 node-seafoam"></div>
        <div class="particle p-8 node-aqua"></div>
        <div class="particle p-9 node-bright-aqua"></div>
        <div class="particle p-10 node-navy"></div>
        <div class="particle p-11 node-gold-accent"></div>
        <div class="particle p-12 node-seafoam"></div>
        <div class="particle p-13 node-aqua"></div>
        <div class="particle p-14 node-bright-seafoam"></div>
        <div class="particle p-15 node-aqua"></div>
      </div>
    </div>

    <div class="sidebar-container">
      <!-- Fixed Brand Area at Top -->
      <div class="sidebar-brand-area">
        <router-link to="/dashboard" class="sidebar-brand-wrapper" @click.native="onNavClick">
          <div class="brand-logo-container">
            <img class="brand-logo" src="/logo.png" alt="NATCON Logo" />
          </div>
          <div class="brand-text-container">
            <span class="brand-title">NATCON</span>
            <span class="brand-subtitle">Events Command Portal</span>
          </div>
        </router-link>
        <div class="sidebar-divider"></div>
      </div>

      <!-- Independently Scrollable Navigation List -->
      <div class="sidebar-nav-scroll">
        <ul class="nav-list" id="accordionSidebar">
          <!-- Dashboard -->
          <li class="nav-item item-stagger-1">
            <router-link
              to="/dashboard"
              class="nav-link"
              active-class="active-link"
              exact
              @click.native="onNavClick"
            >
              <div class="nav-icon-wrapper">
                <i class="fas fa-th-large"></i>
              </div>
              <span class="nav-text">Dashboard</span>
            </router-link>
          </li>

          <!-- Event Operations Section -->
          <li class="section-heading">Event Operations</li>

          <li class="nav-item item-stagger-2">
            <router-link
              to="/amenity/list"
              class="nav-link"
              active-class="active-link"
              @click.native="onNavClick"
            >
              <div class="nav-icon-wrapper">
                <i class="fas fa-concierge-bell"></i>
              </div>
              <span class="nav-text">Amenities</span>
            </router-link>
          </li>

          <li class="nav-item item-stagger-3">
            <router-link
              to="/plan-amenity/list"
              class="nav-link"
              active-class="active-link"
              @click.native="onNavClick"
            >
              <div class="nav-icon-wrapper">
                <i class="fas fa-clipboard-list"></i>
              </div>
              <span class="nav-text">Plan Amenities</span>
            </router-link>
          </li>

          <li class="nav-item item-stagger-4">
            <router-link
              to="/event-participant/list"
              class="nav-link"
              active-class="active-link"
              @click.native="onNavClick"
            >
              <div class="nav-icon-wrapper">
                <i class="fas fa-users"></i>
              </div>
              <span class="nav-text">Participants</span>
              <span class="nav-badge">Live</span>
            </router-link>
          </li>

          <li class="nav-item item-stagger-5">
            <router-link
              to="/idcard/idcard-print-list"
              class="nav-link"
              active-class="active-link"
              @click.native="onNavClick"
            >
              <div class="nav-icon-wrapper">
                <i class="fas fa-id-card"></i>
              </div>
              <span class="nav-text">Print ID Card</span>
            </router-link>
          </li>

          <li class="nav-item item-stagger-6">
            <router-link
              to="/idcard/update-idcard-upload"
              class="nav-link"
              active-class="active-link"
              @click.native="onNavClick"
            >
              <div class="nav-icon-wrapper">
                <i class="fas fa-cloud-upload-alt"></i>
              </div>
              <span class="nav-text">Update ID Card No</span>
            </router-link>
          </li>

          <li class="nav-item item-stagger-7">
            <router-link
              to="/conference-event/list"
              class="nav-link"
              active-class="active-link"
              @click.native="onNavClick"
            >
              <div class="nav-icon-wrapper">
                <i class="fas fa-calendar-alt"></i>
              </div>
              <span class="nav-text">Conference Events</span>
            </router-link>
          </li>

          <li class="nav-item item-stagger-8">
            <router-link
              to="/conference-registration/list"
              class="nav-link"
              active-class="active-link"
              @click.native="onNavClick"
            >
              <div class="nav-icon-wrapper">
                <i class="fas fa-user-check"></i>
              </div>
              <span class="nav-text">Registration</span>
            </router-link>
          </li>

          <li class="nav-item item-stagger-9">
            <router-link
              to="/conference-news/list"
              class="nav-link"
              active-class="active-link"
              @click.native="onNavClick"
            >
              <div class="nav-icon-wrapper">
                <i class="fas fa-newspaper"></i>
              </div>
              <span class="nav-text">News & Updates</span>
            </router-link>
          </li>

          <li class="nav-item item-stagger-10">
            <router-link
              to="/conference-sponsor/list"
              class="nav-link"
              active-class="active-link"
              @click.native="onNavClick"
            >
              <div class="nav-icon-wrapper">
                <i class="fas fa-handshake"></i>
              </div>
              <span class="nav-text">Sponsors</span>
            </router-link>
          </li>

          <!-- Reports Section -->
          <li class="section-heading">Analytics & Reports</li>

          <li class="nav-item item-stagger-11">
            <a
              class="nav-link collapsed"
              href="#"
              data-toggle="collapse"
              data-target="#collapseReports"
              aria-expanded="false"
              aria-controls="collapseReports"
            >
              <div class="nav-icon-wrapper">
                <i class="fas fa-chart-pie"></i>
              </div>
              <span class="nav-text">Reports</span>
              <i class="fas fa-chevron-right collapse-arrow ml-auto"></i>
            </a>
            <div
              id="collapseReports"
              class="collapse submenu-collapse"
              aria-labelledby="headingUtilities"
              data-parent="#accordionSidebar"
            >
              <div class="submenu-container">
                <router-link to="/report/amenity-given" class="submenu-item" @click.native="onNavClick">
                  <i class="far fa-circle mr-2 small"></i>
                  <span>Amenity Given</span>
                </router-link>
                <router-link
                  to="/report/participant-report"
                  class="submenu-item"
                  @click.native="onNavClick"
                >
                  <i class="far fa-circle mr-2 small"></i>
                  <span>Participant Report</span>
                </router-link>
              </div>
            </div>
          </li>

          <!-- Administration Section -->
          <li class="section-heading">Administration</li>

          <li class="nav-item item-stagger-12">
            <router-link
              to="/user/list"
              class="nav-link"
              active-class="active-link"
              @click.native="onNavClick"
            >
              <div class="nav-icon-wrapper">
                <i class="fas fa-user-shield"></i>
              </div>
              <span class="nav-text">User Management</span>
            </router-link>
          </li>
        </ul>
      </div>

      <!-- Fixed Footer Card at Bottom -->
      <div class="sidebar-footer-area">
        <div class="sidebar-footer-card">
          <div class="card-icon">
            <i class="fas fa-award text-gold"></i>
          </div>
          <div class="card-info">
            <span class="info-title">JCI India NATCON</span>
            <span class="info-sub">Official Command Portal</span>
          </div>
        </div>
      </div>
    </div>
  </aside>
</template>

<script>
import companyConfig from "@/company_config.json";

export default {
  name: "SidebarMenu",
  props: {
    toggled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      TestMode: companyConfig.TestMode,
    };
  },
  methods: {
    onNavClick() {
      if (window.innerWidth <= 768) {
        this.$emit("close-sidebar");
      }
    },
  },
};
</script>

<style scoped lang="scss">
/* LUXURY JCI NAVY BLUE FIXED VIEWPORT SIDEBAR */
.natcon-sidebar-wrapper {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  height: 100vh !important;
  max-height: 100vh !important;
  width: 270px;
  background: linear-gradient(
    135deg,
    #6475ce 0%,
    #1b49a3 45%,
    #3a67b1 80%,
    #0097d7 100%
  ) !important;
  z-index: 1050;
  box-shadow: 4px 0 30px rgba(10, 15, 41, 0.3);
  border-right: 1px solid rgba(255, 255, 255, 0.08);
  transition: width 0.3s cubic-bezier(0.25, 0.8, 0.25, 1), top 0.3s ease,
    height 0.3s ease;
  overflow: hidden;

  &.test-mode-active {
    top: 40px !important;
    height: calc(100vh - 40px) !important;
    max-height: calc(100vh - 40px) !important;
  }

  &.toggled {
    width: 90px;

    .brand-text-container,
    .section-heading,
    .nav-text,
    .nav-badge,
    .collapse-arrow,
    .card-info {
      opacity: 0 !important;
      visibility: hidden;
    }
  }
}

/* ==========================================================================
   ANIMATED PARTICLE FLOW BACKGROUND SYSTEM (EXACT CONCEPT 4)
   ========================================================================== */
.sidebar-particle-flow-layer {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  pointer-events: none;
  z-index: 1;
}

/* Layer 1: Flowing Light Streams (SVG Organic Curves) */
.energy-streams-svg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0.75;

  .stream-line {
    fill: none;
    stroke-width: 1.4px;
    stroke-dasharray: 220 450;
    filter: drop-shadow(0 0 4px rgba(255, 255, 255, 0.95))
      drop-shadow(0 0 10px rgba(255, 255, 255, 0.5));
    animation: streamFlow 12s linear infinite;

    &.stream-1 {
      stroke-width: 1.6px;
      stroke-dasharray: 250 500;
      animation-duration: 12s;
      animation-delay: 0s;
    }
    &.stream-2 {
      stroke-width: 1.2px;
      stroke-dasharray: 180 350;
      animation-duration: 16s;
      animation-delay: -3s;
    }
    &.stream-3 {
      stroke-width: 1.8px;
      stroke-dasharray: 300 600;
      animation-duration: 11s;
      animation-delay: -6s;
    }
    &.stream-4 {
      stroke-width: 1.3px;
      stroke-dasharray: 220 450;
      animation-duration: 15s;
      animation-delay: -2s;
    }
    &.stream-5 {
      stroke-width: 1.5px;
      stroke-dasharray: 280 550;
      animation-duration: 18s;
      animation-delay: -9s;
    }
  }
}

/* Layer 2: Network Connection Lines */
.network-lines-svg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;

  .net-line {
    stroke-width: 1px;
    stroke-dasharray: 10 15;
    animation: netPulse 8s ease-in-out infinite alternate;

    &.net-1 {
      animation-delay: 0s;
    }
    &.net-2 {
      animation-delay: -2s;
    }
    &.net-3 {
      animation-delay: -4s;
    }
    &.net-4 {
      animation-delay: -6s;
    }
    &.net-5 {
      animation-delay: -3s;
    }
  }
}

/* Layer 3 & 4: Glowing Particles & Bright Traveling Nodes */
.particles-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;

  .particle {
    position: absolute;
    border-radius: 50%;
    pointer-events: none;
    animation: particleFlowUp 14s linear infinite;

    /* Subtle Energy Trail */
    &::after {
      content: "";
      position: absolute;
      bottom: -8px;
      left: 50%;
      width: 2px;
      height: 14px;
      transform: translateX(-50%);
      background: linear-gradient(to top, transparent, currentColor);
      opacity: 0.55;
    }

    /* Node Color Palette & Glow Highlights */
    &.node-aqua {
      width: 3px;
      height: 3px;
      background: #0097d7;
      color: #0097d7;
      box-shadow: 0 0 8px #0097d7, 0 0 16px rgba(0, 151, 215, 0.6);
    }
    &.node-bright-aqua {
      width: 6px;
      height: 6px;
      background: #ffffff;
      color: #0097d7;
      border: 1px solid #0097d7;
      box-shadow: 0 0 12px #0097d7, 0 0 24px rgba(0, 151, 215, 0.9);
    }
    &.node-seafoam {
      width: 4px;
      height: 4px;
      background: #56bda3;
      color: #56bda3;
      box-shadow: 0 0 10px #56bda3, 0 0 18px rgba(86, 189, 163, 0.6);
    }
    &.node-bright-seafoam {
      width: 5px;
      height: 5px;
      background: #ffffff;
      color: #56bda3;
      border: 1px solid #56bda3;
      box-shadow: 0 0 12px #56bda3, 0 0 20px rgba(86, 189, 163, 0.8);
    }
    &.node-navy {
      width: 3px;
      height: 3px;
      background: #3a67b1;
      color: #3a67b1;
      box-shadow: 0 0 6px #3a67b1;
    }
    &.node-gold {
      width: 3px;
      height: 3px;
      background: #edbe38;
      color: #edbe38;
      box-shadow: 0 0 8px #edbe38;
    }
    &.node-gold-accent {
      width: 5px;
      height: 5px;
      background: #ffffff;
      color: #edbe38;
      border: 1px solid #edbe38;
      box-shadow: 0 0 12px #edbe38, 0 0 22px rgba(237, 190, 56, 0.8);
    }

    /* Individual Particle Positions, Durations & Delays */
    &.p-1 {
      left: 15%;
      animation-duration: 12s;
      animation-delay: 0s;
    }
    &.p-2 {
      left: 35%;
      animation-duration: 16s;
      animation-delay: -3s;
    }
    &.p-3 {
      left: 65%;
      animation-duration: 10s;
      animation-delay: -6s;
    }
    &.p-4 {
      left: 80%;
      animation-duration: 18s;
      animation-delay: -1.5s;
    }
    &.p-5 {
      left: 25%;
      animation-duration: 14s;
      animation-delay: -8s;
    }
    &.p-6 {
      left: 50%;
      animation-duration: 11s;
      animation-delay: -4s;
    }
    &.p-7 {
      left: 70%;
      animation-duration: 15s;
      animation-delay: -9s;
    }
    &.p-8 {
      left: 85%;
      animation-duration: 13s;
      animation-delay: -2s;
    }
    &.p-9 {
      left: 40%;
      animation-duration: 9s;
      animation-delay: -5s;
    }
    &.p-10 {
      left: 10%;
      animation-duration: 20s;
      animation-delay: -11s;
    }
    &.p-11 {
      left: 60%;
      animation-duration: 17s;
      animation-delay: -7s;
    }
    &.p-12 {
      left: 30%;
      animation-duration: 12s;
      animation-delay: -10s;
    }
    &.p-13 {
      left: 75%;
      animation-duration: 14s;
      animation-delay: -12s;
    }
    &.p-14 {
      left: 20%;
      animation-duration: 13s;
      animation-delay: -1s;
    }
    &.p-15 {
      left: 90%;
      animation-duration: 19s;
      animation-delay: -14s;
    }
  }
}

.sidebar-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  max-height: 100%;
  width: 100%;
  padding: 1rem 0.85rem;
  box-sizing: border-box;
  position: relative;
  z-index: 2;
}

/* Fixed Brand Area */
.sidebar-brand-area {
  flex-shrink: 0;
}

.sidebar-brand-wrapper {
  display: flex;
  align-items: center;
  padding: 0.5rem 0.75rem;
  text-decoration: none !important;
}

.brand-logo-container {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.18);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  backdrop-filter: blur(8px);
  transition: transform 0.3s ease;

  .brand-logo {
    width: 28px;
    height: auto;
  }
}

.sidebar-brand-wrapper:hover .brand-logo-container {
  transform: scale(1.08) rotate(-3deg);
  border-color: #edbe38;
}

.brand-text-container {
  margin-left: 0.85rem;
  white-space: nowrap;
  transition: opacity 0.25s ease;

  .brand-title {
    display: block;
    font-family: "Outfit", sans-serif;
    font-size: 1.2rem;
    font-weight: 800;
    color: #ffffff;
    letter-spacing: 0.08em;
    line-height: 1.1;
  }

  .brand-subtitle {
    display: block;
    font-size: 0.68rem;
    font-weight: 700;
    color: #0097d7;
    letter-spacing: 0.05em;
    text-transform: uppercase;
  }
}

.sidebar-divider {
  height: 1px;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.15) 0%,
    rgba(255, 255, 255, 0.02) 100%
  );
  margin: 0.75rem 0.5rem;
}

/* Scrollable Navigation List */
.sidebar-nav-scroll {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  margin: 0.25rem 0;
  padding-right: 2px;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.15);
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: rgba(0, 151, 215, 0.4);
  }
}

.nav-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.section-heading {
  font-size: 0.65rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: rgba(255, 255, 255, 0.45);
  padding: 1.1rem 0.85rem 0.35rem;
  white-space: nowrap;
  transition: opacity 0.25s ease;
}

.nav-item {
  margin-bottom: 0.35rem;
}

.nav-link {
  display: flex;
  align-items: center;
  padding: 0.7rem 0.85rem;
  border-radius: 12px;
  color: rgba(255, 255, 255, 0.78) !important;
  text-decoration: none !important;
  font-weight: 600;
  font-size: 0.875rem;
  transition: all 0.25s cubic-bezier(0.25, 0.8, 0.25, 1);
  position: relative;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
    color: #ffffff !important;
    transform: translateX(3px);

    .nav-icon-wrapper {
      color: #0097d7;
      transform: scale(1.1);
    }
  }
}

.nav-icon-wrapper {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.05rem;
  flex-shrink: 0;
  transition: all 0.25s ease;
  color: rgba(255, 255, 255, 0.85);
}

.nav-text {
  margin-left: 0.85rem;
  white-space: nowrap;
  transition: opacity 0.25s ease;
}

.nav-badge {
  margin-left: auto;
  font-size: 0.65rem;
  font-weight: 800;
  padding: 0.15rem 0.55rem;
  border-radius: 10px;
  background: #0097d7;
  color: #ffffff;
  transition: opacity 0.25s ease;
}

.collapse-arrow {
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.5);
  transition: opacity 0.25s ease, transform 0.25s ease;
}

.nav-link:not(.collapsed) .collapse-arrow {
  transform: rotate(90deg);
  color: #0097d7;
}

/* Active Navy Blue Menu Pill */
.active-link {
  background: linear-gradient(
    90deg,
    rgba(0, 151, 215, 0.25) 0%,
    rgba(58, 103, 177, 0.15) 100%
  );
  border: 1px solid rgba(0, 151, 215, 0.4);
  color: #ffffff !important;
  font-weight: 800;
  box-shadow: 0 4px 15px rgba(0, 151, 215, 0.2);

  .nav-icon-wrapper {
    color: #edbe38;
    background: rgba(237, 190, 56, 0.15);
  }

  &::before {
    content: "";
    position: absolute;
    left: 0;
    top: 20%;
    bottom: 20%;
    width: 4px;
    background: #edbe38;
    border-radius: 0 4px 4px 0;
  }
}

/* Submenu */
.submenu-container {
  padding: 0.4rem 0 0.4rem 2.8rem;
}

.submenu-item {
  display: flex;
  align-items: center;
  padding: 0.45rem 0.75rem;
  font-size: 0.82rem;
  color: rgba(255, 255, 255, 0.65);
  text-decoration: none !important;
  border-radius: 8px;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover,
  &.router-link-active {
    color: #0097d7;
    background: rgba(255, 255, 255, 0.05);
    font-weight: 700;

    i {
      color: #edbe38;
    }
  }
}

/* Fixed Bottom Footer Card */
.sidebar-footer-area {
  flex-shrink: 0;
  margin-top: auto;
  padding-top: 0.5rem;
}

.sidebar-footer-card {
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 14px;
  padding: 0.75rem;
  display: flex;
  align-items: center;
  white-space: nowrap;

  .card-icon {
    width: 36px;
    height: 36px;
    border-radius: 10px;
    background: rgba(237, 190, 56, 0.15);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.1rem;
    flex-shrink: 0;

    .text-gold {
      color: #edbe38;
    }
  }

  .card-info {
    margin-left: 0.75rem;
    transition: opacity 0.25s ease;

    .info-title {
      display: block;
      font-size: 0.78rem;
      font-weight: 800;
      color: #ffffff;
    }
    .info-sub {
      display: block;
      font-size: 0.65rem;
      color: rgba(255, 255, 255, 0.55);
      font-weight: 600;
    }
  }
}

/* Stagger Animations for Menu Load */
@keyframes menuFadeIn {
  0% {
    opacity: 0;
    transform: translateX(-8px);
  }
  100% {
    opacity: 1;
    transform: translateX(0);
  }
}

.item-stagger-1 {
  animation: menuFadeIn 0.3s ease 0ms forwards;
}
.item-stagger-2 {
  animation: menuFadeIn 0.3s ease 30ms forwards;
}
.item-stagger-3 {
  animation: menuFadeIn 0.3s ease 60ms forwards;
}
.item-stagger-4 {
  animation: menuFadeIn 0.3s ease 90ms forwards;
}
.item-stagger-5 {
  animation: menuFadeIn 0.3s ease 120ms forwards;
}
.item-stagger-6 {
  animation: menuFadeIn 0.3s ease 150ms forwards;
}
.item-stagger-7 {
  animation: menuFadeIn 0.3s ease 180ms forwards;
}
.item-stagger-8 {
  animation: menuFadeIn 0.3s ease 210ms forwards;
}
.item-stagger-9 {
  animation: menuFadeIn 0.3s ease 240ms forwards;
}
.item-stagger-10 {
  animation: menuFadeIn 0.3s ease 270ms forwards;
}
.item-stagger-11 {
  animation: menuFadeIn 0.3s ease 300ms forwards;
}
.item-stagger-12 {
  animation: menuFadeIn 0.3s ease 330ms forwards;
}

@keyframes streamFlow {
  0% {
    stroke-dashoffset: 1200;
    opacity: 0.3;
    filter: drop-shadow(0 0 2px rgba(255, 255, 255, 0.4));
  }
  50% {
    opacity: 0.95;
    filter: drop-shadow(0 0 6px rgba(255, 255, 255, 1))
      drop-shadow(0 0 14px rgba(255, 255, 255, 0.85));
  }
  100% {
    stroke-dashoffset: -1200;
    opacity: 0.3;
    filter: drop-shadow(0 0 2px rgba(255, 255, 255, 0.4));
  }
}

@keyframes netPulse {
  0% {
    opacity: 0.1;
    stroke-dashoffset: 0;
  }
  50% {
    opacity: 0.45;
  }
  100% {
    opacity: 0.1;
    stroke-dashoffset: 50;
  }
}

@keyframes particleFlowUp {
  0% {
    transform: translateY(105vh) translateX(0) scale(0.6);
    opacity: 0;
  }
  15% {
    opacity: 0.85;
  }
  50% {
    transform: translateY(50vh) translateX(22px) scale(1.2);
    opacity: 0.95;
  }
  85% {
    opacity: 0.85;
  }
  100% {
    transform: translateY(-80px) translateX(-18px) scale(0.5);
    opacity: 0;
  }
}

/* Reduced Motion Support for Accessibility */
@media (prefers-reduced-motion: reduce) {
  .stream-line,
  .net-line,
  .particle {
    animation: none !important;
    opacity: 0.4 !important;
  }
}

/* Mobile Responsiveness */
@media (max-width: 768px) {
  .natcon-sidebar-wrapper {
    width: 0 !important;
    min-width: 0 !important;

    &.toggled {
      width: 270px !important;
      min-width: 270px !important;

      .brand-text-container,
      .section-heading,
      .nav-text,
      .nav-badge,
      .collapse-arrow,
      .sidebar-footer-card .card-info {
        opacity: 1 !important;
        visibility: visible !important;
      }
    }
  }
}
</style>
